// Small shared pieces for the engineering flow UI.
import React, { useEffect, useRef, useState } from 'react';

// A code block with a Copy button in its corner. `copy` is what gets copied (defaults to the shown text).
export function CopyCode({ code, copy, className = 'code' }) {
  const [done, setDone] = useState(false);
  const t = useRef(null);
  useEffect(() => () => clearTimeout(t.current), []);
  const go = async () => {
    try { await navigator.clipboard.writeText(copy ?? code); setDone(true); clearTimeout(t.current); t.current = setTimeout(() => setDone(false), 1500); } catch (e) { /* no clipboard: the text is selectable */ }
  };
  return <div className="ef-copywrap"><pre className={className}>{code}</pre>
    <button type="button" className="ef-copy" onClick={go} aria-label="Copy to clipboard">{done ? 'Copied ✓' : 'Copy'}</button></div>;
}

export const pct = (x) => (x == null ? '—' : Math.round(x * 100) + '%');
export const col = (x) => (x == null ? 'var(--ink3)' : x >= 0.75 ? 'var(--pass)' : x >= 0.5 ? 'var(--warn)' : 'var(--fail)');
export const secs = (ms) => (ms == null ? '—' : (ms / 1000).toFixed(1) + 's');
export const usd = (x) => (x == null ? '—' : x === 0 ? '$0' : '$' + (x < 0.001 ? Number(x).toPrecision(2) : Number(x).toFixed(4)));

const LBL = { PASS: ['Pass', 'p'], WARN: ['Warn', 'w'], FAIL: ['Fail', 'f'] };
export function St({ label, status }) {
  if (!label && status === 'error') return <span className="ef-st f" title="No answer: the run errored">Error</span>;
  if (!label) return <span className="ef-st n">…</span>;
  const [t, c] = LBL[label] || [label, 'n'];
  return <span className={'ef-st ' + c}>{t}</span>;
}

export function Kind({ kind, newly }) {   // newly: what a case failing on both sides newly gets wrong on the candidate
  if (kind === 'regressed') return <span className="ef-st reg">Regressed</span>;
  if (kind === 'errored') return <span className="ef-st reg" title="The new version failed to answer">Errored</span>;
  if (kind === 'fixed') return <span className="ef-st fix">Fixed</span>;
  if (kind === 'worse') return <span className="ef-st w">Got worse</span>;
  if (kind === 'pending') return <span className="xs fnt">running…</span>;
  if (newly?.length) return <span className="ef-st w" title={'Fails on both, but the new version newly fails: ' + newly.join('; ')}>New failure</span>;
  return <span className="xs fnt">same</span>;
}

export function Bar({ x }) {
  return (
    <span>
      <span className="ef-minibar"><i style={{ width: (x || 0) * 100 + '%', background: col(x) }} /></span>
      <span className="ef-score" style={{ color: col(x) }}>{x == null ? '—' : x.toFixed(2)}</span>
    </span>
  );
}

export function Kpi({ l, v, d, dir }) {
  return (
    <div className="ef-card ef-kpi">
      <div className="l">{l}</div>
      <div className="v">{v}</div>
      {d && <div className={'d ' + (dir || '')}>{d}</div>}
    </div>
  );
}

export function NextBar({ why, children }) {
  return (
    <div className="ef-next">
      <span className="why">{why}</span>
      <span className="go">{children}</span>
    </div>
  );
}

export function Err({ e }) {
  return e ? <div className="ef-err">{String(e.message || e)}</div> : null;
}

// Line diff (LCS) for a live preview before the version exists on the server.
export function diffLines(a, b) {
  const A = (a || '').split('\n'), B = (b || '').split('\n');
  const n = A.length, m = B.length;
  const L = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--)
    L[i][j] = A[i] === B[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
  const out = []; let i = 0, j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) { out.push({ op: ' ', text: A[i] }); i++; j++; }
    else if (L[i + 1][j] >= L[i][j + 1]) { out.push({ op: '-', text: A[i] }); i++; }
    else { out.push({ op: '+', text: B[j] }); j++; }
  }
  while (i < n) out.push({ op: '-', text: A[i++] });
  while (j < m) out.push({ op: '+', text: B[j++] });
  return out;
}

export function Diff({ lines }) {
  return (
    <div className="ef-diff">
      {lines.map((l, i) => (
        <div key={i} className={l.op === '+' ? 'a' : l.op === '-' ? 'r' : ''}>{(l.op === ' ' ? '  ' : l.op + ' ') + l.text}</div>
      ))}
    </div>
  );
}

// Minimal Markdown → React for the generated report (headings, tables, bullets, bold, code).
function inline(s) {
  return s.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((p, i) =>
    p.startsWith('**') ? <b key={i}>{p.slice(2, -2)}</b> : p.startsWith('`') ? <code key={i}>{p.slice(1, -1)}</code> : p);
}
export function Markdown({ text }) {
  const lines = (text || '').split('\n'); const out = []; let k = 0;
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (l.startsWith('# ')) out.push(<h3 key={k++} style={{ margin: '0 0 6px' }}>{inline(l.slice(2))}</h3>);
    else if (l.startsWith('## ')) out.push(<h2 key={k++}>{inline(l.slice(3))}</h2>);
    else if (l.startsWith('|')) {
      const rows = []; while (i < lines.length && lines[i].startsWith('|')) { rows.push(lines[i]); i++; } i--;
      const cells = (r) => r.split('|').slice(1, -1).map((c) => c.trim());
      out.push(<table key={k++}><tbody>{rows.filter((r) => !/^\|[-| ]+\|$/.test(r)).map((r, ri) =>
        <tr key={ri}>{cells(r).map((c, ci) => ri === 0 ? <th key={ci}>{inline(c)}</th> : <td key={ci}>{inline(c)}</td>)}</tr>)}</tbody></table>);
    } else if (l.startsWith('- ')) out.push(<div key={k++} className="sm" style={{ margin: '2px 0' }}>• {inline(l.slice(2))}</div>);
    else if (l.trim()) out.push(<p key={k++} className="sm" style={{ margin: '4px 0' }}>{inline(l)}</p>);
  }
  return <div className="ef-report">{out}</div>;
}
